// @ts-nocheck
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Plus, Edit, Trash, Users, Settings, Crown, Building } from 'lucide-react';

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  active: boolean;
  created_at: string;
  member_count?: number;
  org_type: 'master' | 'standard';
  parent_org_id?: string;
  is_active: boolean;
}

interface OrgFormData {
  name: string;
  slug: string;
  description: string;
  ownerEmail: string;
}

export default function Organizacoes() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [formData, setFormData] = useState<OrgFormData>({
    name: '',
    slug: '',
    description: '',
    ownerEmail: ''
  });

  const { hasPermission, isSuperAdmin } = usePermissions();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check if user is admin of "Grupo Ares" organization specifically
  const { data: isGrupoAresAdmin = false } = useQuery({
    queryKey: ['isGrupoAresAdmin', user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      console.log('🔍 Verificando se usuário é admin do Grupo Ares:', user.email);
      
      const { data, error } = await supabase
        .from('org_memberships')
        .select('role, org_id')
        .eq('user_id', user.id)
        .eq('org_id', '00000000-0000-0000-0000-000000000001') // Grupo Ares ID
        .eq('active', true)
        .in('role', ['owner', 'admin'])
        .maybeSingle();

      console.log('🔍 Resultado da query de admin Grupo Ares:', { data, error });
      
      const result = !!data;
      console.log('🔍 É admin do Grupo Ares?', result);
      return result;
    },
    enabled: !!user
  });

  // Fetch organizations - always call hooks first
  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ['organizations', isGrupoAresAdmin, user?.id],
    queryFn: async () => {
      console.log('🔍 Buscando organizações - isGrupo AresAdmin:', isGrupoAresAdmin);
      
      if (isGrupoAresAdmin) {
        const { data, error } = await supabase
          .from('organizations')
          .select(`
            *,
            org_memberships(count)
          `)
          .order('name');

        if (error) {
          console.error('❌ Erro ao buscar organizações:', error);
          throw error;
        }

        const result = data.map(org => ({
          ...org,
          member_count: org.org_memberships?.[0]?.count || 0
        }));
        console.log('📊 Organizações encontradas (Grupo Ares Admin):', result);
        return result;
      }

      // Buscar apenas organizações do usuário logado
      const { data: memberships, error: memErr } = await supabase
        .from('org_memberships')
        .select('org_id')
        .eq('user_id', user!.id)
        .eq('active', true);

      if (memErr) {
        console.error('❌ Erro ao buscar memberships:', memErr);
        throw memErr;
      }

      const orgIds = (memberships || []).map((m: any) => m.org_id).filter(Boolean);

      if (orgIds.length === 0) {
        console.log('ℹ️ Usuário não possui organizações ativas.');
        return [];
      }

      const { data, error } = await supabase
        .from('organizations')
        .select(`
          *,
          org_memberships(count)
        `)
        .in('id', orgIds)
        .order('name');

      if (error) {
        console.error('❌ Erro ao buscar organizações do usuário:', error);
        throw error;
      }

      const result = data.map(org => ({
        ...org,
        member_count: org.org_memberships?.[0]?.count || 0
      }));
      
      console.log('📊 Organizações do usuário:', result);
      return result;
    },
    enabled: (hasPermission('admin', 'organizacoes', 'read') || isGrupoAresAdmin) && !!user
  });

  // Create organization mutation
  const createOrgMutation = useMutation({
    mutationFn: async (data: OrgFormData) => {
      const { data: result, error } = await supabase.rpc('create_organization', {
        _name: data.name,
        _slug: data.slug || null
      });

      if (error) throw error;

      // Update description if provided
      if (data.description) {
        const { error: updateError } = await supabase
          .from('organizations')
          .update({ description: data.description })
          .eq('id', result);

        if (updateError) throw updateError;
      }

      // Add the specified owner to the organization
      if (data.ownerEmail) {
        // Get user ID by email
        const { data: userData, error: userError } = await supabase
          .rpc('get_user_id_by_email', { user_email: data.ownerEmail });

        if (userError) throw new Error(`Usuário não encontrado: ${data.ownerEmail}`);
        if (!userData) throw new Error(`Usuário não encontrado: ${data.ownerEmail}`);

        // Add user as owner
        const { error: memberError } = await supabase
          .from('org_memberships')
          .insert({
            org_id: result,
            user_id: userData,
            role: 'owner',
            invited_by: user?.id,
            created_by: user?.id,
            updated_by: user?.id
          });

        if (memberError) throw memberError;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setIsCreateModalOpen(false);
      setFormData({ name: '', slug: '', description: '', ownerEmail: '' });
      toast({
        title: "Organização criada",
        description: "A organização foi criada com sucesso."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao criar organização",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Update organization mutation
  const updateOrgMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<OrgFormData> }) => {
      const { error } = await supabase
        .from('organizations')
        .update(data)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setEditingOrg(null);
      setFormData({ name: '', slug: '', description: '', ownerEmail: '' });
      toast({
        title: "Organização atualizada",
        description: "A organização foi atualizada com sucesso."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar organização",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Toggle organization status
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase
        .from('organizations')
        .update({ active })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: "Status atualizado",
        description: "O status da organização foi atualizado."
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingOrg) {
      updateOrgMutation.mutate({ id: editingOrg.id, data: formData });
    } else {
      createOrgMutation.mutate(formData);
    }
  };

  const startEdit = (org: Organization) => {
    setEditingOrg(org);
        setFormData({
          name: org.name,
          slug: org.slug,
          description: org.description || '',
          ownerEmail: ''
        });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check permissions after all hooks are called
  if (!hasPermission('admin', 'organizacoes', 'read') && !isGrupoAresAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-medium">Acesso Negado</h3>
          <p className="text-muted-foreground">Você não tem permissão para acessar este módulo.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    console.log('🔄 Carregando organizações...');
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  console.log('📊 Estado final - isGrupoAresAdmin:', isGrupoAresAdmin, 'organizações:', organizations.length);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">Organizações</h1>
            {isSuperAdmin() && (
              <Badge variant="outline" className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
                <Crown className="h-3 w-3 mr-1 text-yellow-600" />
                Super Admin
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            {isGrupoAresAdmin 
              ? "Controle master de organizações - Administradores do Grupo Ares podem criar e administrar todas as organizações"
              : "Gerencie organizações e controle de acesso multi-tenant"
            }
          </p>
        </div>
        {isGrupoAresAdmin && (
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Nova Organização
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar Nova Organização</DialogTitle>
              <DialogDescription>
                Crie uma nova organização para isolamento de dados.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      name,
                      slug: generateSlug(name)
                    }));
                  }}
                  placeholder="Nome da organização"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="slug-da-organizacao"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição da organização"
                />
              </div>
              
              <div>
                <Label htmlFor="ownerEmail">E-mail do Proprietário</Label>
                <Input
                  id="ownerEmail"
                  type="email"
                  value={formData.ownerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, ownerEmail: e.target.value }))}
                  placeholder="email@proprietario.com"
                  required
                />
              </div>
              
              <DialogFooter>
                <Button type="submit" disabled={createOrgMutation.isPending}>
                  {createOrgMutation.isPending ? 'Criando...' : 'Criar Organização'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Organizações</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{organizations.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organizações Ativas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {organizations.filter(org => org.active).length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Membros</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {organizations.reduce((acc, org) => acc + (org.member_count || 0), 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Pesquisar Organizações</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Buscar por nome ou slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </CardContent>
      </Card>

      {/* Organizations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Organizações Cadastradas</CardTitle>
          <CardDescription>
            Lista de todas as organizações no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Membros</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criada em</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrganizations.map((org) => (
                 <TableRow key={org.id}>
                   <TableCell className="font-medium">
                     <div className="flex items-center gap-2">
                       {org.org_type === 'master' && (
                         <Crown className="h-4 w-4 text-yellow-600" />
                       )}
                       {org.name}
                       {org.org_type === 'master' && (
                         <Badge variant="outline" size="sm" className="text-xs bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
                           Master
                         </Badge>
                       )}
                     </div>
                   </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">
                      {org.slug}
                    </code>
                  </TableCell>
                  <TableCell>
                    {org.description ? (
                      <span className="text-sm text-muted-foreground">
                        {org.description.substring(0, 50)}
                        {org.description.length > 50 && '...'}
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">Sem descrição</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {org.member_count || 0} membros
                    </Badge>
                  </TableCell>
                   <TableCell>
                     {isGrupoAresAdmin ? (
                       <Button
                         variant="ghost"
                         size="sm"
                         onClick={() => toggleStatusMutation.mutate({
                           id: org.id,
                           active: !org.active
                         })}
                       >
                         <Badge variant={org.active ? "default" : "secondary"}>
                           {org.active ? 'Ativa' : 'Inativa'}
                         </Badge>
                       </Button>
                     ) : (
                       <Badge variant={org.active ? "default" : "secondary"}>
                         {org.active ? 'Ativa' : 'Inativa'}
                       </Badge>
                     )}
                   </TableCell>
                  <TableCell>
                    {new Date(org.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                   <TableCell>
                     <div className="flex gap-2">
                       {isGrupoAresAdmin && (
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => startEdit(org)}
                         >
                           <Edit className="h-4 w-4" />
                         </Button>
                       )}
                       <Button
                         variant="ghost"
                         size="sm"
                         onClick={() => {
                           // Navigate to members management
                           window.location.href = `/intranet/organizacoes/${org.id}/membros`;
                         }}
                       >
                         <Users className="h-4 w-4" />
                       </Button>
                       {isGrupoAresAdmin && org.name !== 'Grupo Ares' && (
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => {
                             if (confirm(`Tem certeza que deseja excluir a organização "${org.name}"?`)) {
                               // TODO: Implement delete functionality
                               toast({
                                 title: "Funcionalidade em desenvolvimento",
                                 description: "A exclusão de organizações será implementada em breve.",
                                 variant: "destructive"
                               });
                             }
                           }}
                         >
                           <Trash className="h-4 w-4" />
                         </Button>
                       )}
                     </div>
                   </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={!!editingOrg} onOpenChange={() => setEditingOrg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Organização</DialogTitle>
            <DialogDescription>
              Atualize as informações da organização.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Nome</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nome da organização"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-slug">Slug</Label>
              <Input
                id="edit-slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="slug-da-organizacao"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-description">Descrição</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrição da organização"
              />
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setEditingOrg(null)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={updateOrgMutation.isPending}>
                {updateOrgMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}